import React, { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  Users,
  UserPlus,
  FileText,
  CheckCircle2,
  Clock,
  Ban,
  RefreshCw,
  BarChart2,
} from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import {
  getDashboard,
  type DashboardWidget,
  type DonutStat,
  type TrendGranularity,
} from "../../services/dashboardService";
import { getWidgetUIConfig } from "../../constants/dashboardWidgetRegistry";
import MDTDonut from "./MDTDonut";
import OverviewStatStrip from "./OverviewStatStrip";
import OnboardingLineChart from "./OnboardingLineChart";
import {
  KpiCardSkeleton,
  ChartSkeleton,
  DonutSkeleton,
  TableSkeleton,
} from "../Common/Skeleton";
import WidgetErrorState from "../Common/WidgetErrorState";
import EmptyState from "../Common/EmptyState";
import StaleDataBanner from "../Common/StaleDataBanner";
import { handleApiError } from "../../utils/errorHandler";

const timeAgo = (dateString?: string | null) => {
  if (!dateString) return "—";
  const now = new Date().getTime();
  const past = new Date(dateString).getTime();
  const diff = Math.floor((now - past) / 1000);
  if (diff < 60) return `${diff}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)} min ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)} hour ago`;
  return `${Math.floor(diff / 86400)} day ago`;
};

/** Don't render raw encrypted/JSON payloads from backend; show a safe placeholder. */
function safeDisplay(value: unknown): string | null {
  if (value == null) return null;
  const s = typeof value === "string" ? value : String(value);
  if (s.trim() === "") return null;
  if (s.includes('"iv"') && s.includes('"data"')) return null;
  try {
    const parsed = JSON.parse(s);
    if (parsed && typeof parsed === "object" && ("iv" in parsed || "data" in parsed)) return null;
  } catch {
    // not JSON, use as-is if not encrypted-looking
  }
  return s;
}

/** Greeting name: fallback when missing or an encrypted payload. */
function formatGreetingName(name: unknown): string {
  const cleaned = safeDisplay(name);
  return cleaned && cleaned.trim() ? cleaned.trim() : "Doctor";
}

/** Normalize backend audit item to Recent Activity shape. */
function mapItemToRecentActivity(item: unknown): {
  id: string | number;
  action: string;
  subtitle: string;
  createdAt: string | null;
  type: "patient" | "user" | "report" | "note";
} | null {
  if (item == null || typeof item !== "object") return null;
  const o = item as Record<string, unknown>;
  const title = safeDisplay(o.description ?? o.action ?? o.title ?? o.name) ?? "";
  const actorName = safeDisplay(o.actorName);
  const targetName = safeDisplay(o.targetName);
  const subtitle = [actorName, targetName].filter(Boolean).join(" • ");
  const createdAt = o.created_at ?? o.createdAt ?? o.timestamp;
  let type: "patient" | "user" | "report" | "note" = "note";
  const lower = (title + " " + (o.entity ?? "")).toLowerCase();
  if (lower.includes("patient")) type = "patient";
  else if (lower.includes("doctor") || lower.includes("user")) type = "user";
  else if (lower.includes("imaging") || lower.includes("histology") || lower.includes("report")) type = "report";
  return {
    id: (o.id as string | number) ?? 0,
    action: title || "—",
    subtitle,
    createdAt: createdAt != null ? String(createdAt) : null,
    type,
  };
}

/** Recent Activity UI: icon by type, action, subtitle, timestamp. */
function RecentActivityOldUI({
  items,
  viewAllHref,
  onViewAll,
  twoColumn = false,
}: {
  items: unknown[];
  viewAllHref?: string;
  onViewAll: () => void;
  twoColumn?: boolean;
}) {
  const normalized = items
    .map(mapItemToRecentActivity)
    .filter((x): x is NonNullable<typeof x> => x != null);
  const byDate = [...normalized].sort((a, b) => {
    if (!a.createdAt || !b.createdAt) return 0;
    return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
  });
  const latest = byDate.slice(0, twoColumn ? 8 : 5);

  return (
    <div className="card overflow-hidden min-w-0">
      <div className="flex items-center justify-between gap-2 mb-4 min-w-0">
        <h3 className="text-lg font-semibold text-neutral-900 min-w-0 truncate">Recent Activity</h3>
        {viewAllHref && (
          <button
            type="button"
            onClick={onViewAll}
            className="text-primary-600 hover:text-primary-700 text-sm font-medium flex-shrink-0"
          >
            View all
          </button>
        )}
      </div>
      {twoColumn ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 min-w-0">
          {latest.length === 0 ? (
            <div className="col-span-full">
              <EmptyState
                icon={<Users size={22} />}
                title="No recent activity"
                description="No recent activity logs or events recorded yet."
                variant="compact"
              />
            </div>
          ) : (
            latest.map((activity) => (
              <div
                key={activity.id}
                className="flex items-center space-x-3 p-3 bg-neutral-50 rounded-lg min-w-0 overflow-hidden"
              >
                <div
                  className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                    activity.type === "patient"
                      ? "bg-primary-100"
                      : activity.type === "report"
                        ? "bg-primary-100"
                        : activity.type === "note"
                          ? "bg-success-50"
                          : "bg-neutral-100"
                  }`}
                >
                  {activity.type === "patient" && <Users className="h-4 w-4 text-primary-600" />}
                  {activity.type === "report" && <FileText className="h-4 w-4 text-primary-600" />}
                  {activity.type === "note" && <FileText className="h-4 w-4 text-success-500" />}
                  {activity.type === "user" && <UserPlus className="h-4 w-4 text-neutral-600" />}
                </div>
                <div className="flex-1 min-w-0 overflow-hidden">
                  <p className="font-medium text-neutral-900 line-clamp-2 break-words">{activity.action}</p>
                  {activity.subtitle ? (
                    <p className="text-sm text-neutral-600 line-clamp-2 break-words mt-0.5">{activity.subtitle}</p>
                  ) : null}
                  {activity.createdAt ? (
                    <p className="text-xs text-neutral-500 mt-1">{timeAgo(activity.createdAt)}</p>
                  ) : null}
                </div>
              </div>
            ))
          )}
        </div>
      ) : (
        <div className="space-y-3 min-w-0">
          {latest.length === 0 ? (
            <EmptyState
              icon={<Users size={22} />}
              title="No recent activity"
              description="No recent activity logs or events recorded yet."
              variant="compact"
            />
          ) : (
            latest.map((activity) => (
              <div
                key={activity.id}
                className="flex items-center space-x-3 p-3 bg-neutral-50 rounded-lg min-w-0 overflow-hidden"
              >
                <div
                  className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                    activity.type === "patient"
                      ? "bg-primary-100"
                      : activity.type === "report"
                        ? "bg-primary-100"
                        : activity.type === "note"
                          ? "bg-success-50"
                          : "bg-neutral-100"
                  }`}
                >
                  {activity.type === "patient" && <Users className="h-4 w-4 text-primary-600" />}
                  {activity.type === "report" && <FileText className="h-4 w-4 text-primary-600" />}
                  {activity.type === "note" && <FileText className="h-4 w-4 text-success-500" />}
                  {activity.type === "user" && <UserPlus className="h-4 w-4 text-neutral-600" />}
                </div>
                <div className="flex-1 min-w-0 overflow-hidden">
                  <p className="font-medium text-neutral-900 line-clamp-2 break-words">{activity.action}</p>
                  {activity.subtitle ? (
                    <p className="text-sm text-neutral-600 line-clamp-2 break-words mt-0.5">{activity.subtitle}</p>
                  ) : null}
                  {activity.createdAt ? (
                    <p className="text-xs text-neutral-500 mt-1">{timeAgo(activity.createdAt)}</p>
                  ) : null}
                </div>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}

/** Generic LIST widget item. */
function ListWidgetItem({ item }: { item: unknown }) {
  if (item == null) return null;
  const o = typeof item === "object" && item !== null ? (item as Record<string, unknown>) : {};
  const actionStr = safeDisplay(o.action ?? o.title ?? o.name) ?? "—";
  const actorName = safeDisplay(o.actorName);
  const targetName = safeDisplay(o.targetName);
  const subtitle = [actorName, targetName].filter(Boolean).join(" • ");
  const createdAt = o.created_at ?? o.createdAt ?? o.timestamp;
  const createdAtStr = createdAt != null ? timeAgo(String(createdAt)) : null;

  return (
    <div className="flex items-center space-x-3 p-3 bg-neutral-50 rounded-lg min-w-0 overflow-hidden">
      <div className="flex-1 min-w-0 overflow-hidden">
        <p className="font-medium text-neutral-900 line-clamp-2 break-words">{actionStr}</p>
        {subtitle ? <p className="text-sm text-neutral-600 line-clamp-2 break-words mt-0.5">{subtitle}</p> : null}
        {createdAtStr ? <p className="text-xs text-neutral-500 mt-1">{createdAtStr}</p> : null}
      </div>
    </div>
  );
}

/** Map backend stat icon key → Lucide icon element for a donut's stat strip. */
function statIconElement(widgetKey: string, iconKey: string) {
  if (iconKey === "approved") return <CheckCircle2 size={14} aria-hidden="true" />;
  if (iconKey === "rejected") return <Ban size={14} aria-hidden="true" />;
  if (iconKey === "pending") return <Clock size={14} aria-hidden="true" />;
  const isEntity = widgetKey.includes("PATIENT");
  return isEntity ? <Users size={14} aria-hidden="true" /> : <FileText size={14} aria-hidden="true" />;
}

/** Doctor Dashboard for ALPHA, matching the LYMPHO doctor dashboard. */
const DoctorDashboard: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [loading, setLoading] = useState<boolean>(true);
  const [widgets, setWidgets] = useState<DashboardWidget[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [isStale, setIsStale] = useState<boolean>(false);
  const [trendGranularity, setTrendGranularity] = useState<Record<string, TrendGranularity>>({});

  const requestIdRef = useRef<number>(0);

  const fetchWidgets = useCallback(async () => {
    const requestId = ++requestIdRef.current;
    setLoading(true);

    try {
      const { widgets } = await getDashboard();
      if (requestId !== requestIdRef.current) return;
      setWidgets(widgets);
      setError(null);
      setIsStale(false);
    } catch (err: unknown) {
      if (requestId !== requestIdRef.current) return;
      const apiErr = handleApiError(err);
      setError(apiErr.message || "Failed to load dashboard");
      if (widgets.length > 0) {
        setIsStale(true);
      }
    } finally {
      if (requestId === requestIdRef.current) {
        setLoading(false);
      }
    }
  }, [widgets.length]);

  useEffect(() => {
    fetchWidgets();
  }, [fetchWidgets]);

  const handleRetry = () => {
    fetchWidgets();
  };

  // 1. Initial Loading State (No cached widgets)
  if (loading && widgets.length === 0) {
    return (
      <div className="space-y-6" data-testid="dashboard-loading-skeleton">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-[#10253A]">
              Hi, {formatGreetingName(user?.name)}
            </h1>
            <p className="text-sm text-neutral-500 mt-1">Here's what's happening with your work today.</p>
          </div>
        </div>

        {/* KPI Skeleton Cards */}
        <KpiCardSkeleton count={4} />

        {/* Chart / Donut Skeletons */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <DonutSkeleton />
          <ChartSkeleton height={220} />
        </div>

        {/* Tabular / List Skeletons */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <TableSkeleton rows={4} columns={2} />
          <TableSkeleton rows={4} columns={2} />
        </div>
      </div>
    );
  }

  // 2. Initial Error State (Failed before any data could be cached)
  if (error && widgets.length === 0) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-[#10253A]">
            Hi, {formatGreetingName(user?.name)}
          </h1>
          <p className="text-sm text-neutral-500 mt-1">Here's what's happening with your work today.</p>
        </div>

        <WidgetErrorState
          title="Dashboard Unavailable"
          message={error}
          onRetry={handleRetry}
          isRetrying={loading}
          variant="card"
        />
      </div>
    );
  }

  // 3. Empty Dashboard State (Response succeeded with 0 widgets)
  if (!loading && widgets.length === 0) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-[#10253A]">
            Hi, {formatGreetingName(user?.name)}
          </h1>
          <p className="text-sm text-neutral-500 mt-1">Here's what's happening with your work today.</p>
        </div>

        <div className="card py-12">
          <EmptyState
            icon={<BarChart2 size={28} />}
            title="No Dashboard Widgets Available"
            description="No widgets have been assigned to your role or configured for this dashboard yet."
            variant="default"
          />
        </div>
      </div>
    );
  }

  const statWidgets = widgets.filter((w): w is Extract<DashboardWidget, { type: "STAT" }> => w.type === "STAT");
  const listWidgets = widgets.filter((w): w is Extract<DashboardWidget, { type: "LIST" }> => w.type === "LIST");
  const donutWidgets = widgets.filter((w): w is Extract<DashboardWidget, { type: "DONUT" }> => w.type === "DONUT");
  const trendWidgets = widgets.filter((w): w is Extract<DashboardWidget, { type: "TREND" }> => w.type === "TREND");
  const chartWidgets = [...donutWidgets, ...trendWidgets];

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-[#10253A]">
            Hi, {formatGreetingName(user?.name)}
          </h1>
          <p className="text-sm text-neutral-500 mt-1">Here's what's happening with your work today.</p>
        </div>

        <div className="flex items-center gap-2 self-start sm:self-auto">
          <button
            type="button"
            onClick={handleRetry}
            disabled={loading}
            className="inline-flex items-center gap-2 rounded-lg border border-[#E4EAF0] bg-white px-3.5 py-2 text-xs font-semibold text-[#10253A] shadow-xs hover:bg-neutral-50 transition-colors disabled:opacity-60 cursor-pointer"
          >
            <RefreshCw size={13} className={loading ? "animate-spin text-[#15BBAE]" : "text-[#15BBAE]"} />
            <span>Refresh</span>
          </button>
        </div>
      </div>

      {/* Stale Data Notification if background refresh fails */}
      {isStale && (
        <StaleDataBanner
          message="Unable to refresh latest dashboard data. Showing previously loaded information."
          onRetry={handleRetry}
          isRetrying={loading}
          onDismiss={() => setIsStale(false)}
        />
      )}

      {/* KPI Cards */}
      {statWidgets.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {statWidgets.map((w) => {
            const config = getWidgetUIConfig(w.key, w.title);
            const Icon = config.icon;
            return (
              <div key={w.key} className="card">
                <div className="flex items-center">
                  <div className={`p-3 rounded-lg ${config.bgColor}`}>
                    <Icon className={`h-6 w-6 ${config.color}`} />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-neutral-600">{config.title}</p>
                    <p className="text-2xl font-bold text-neutral-900">{w.value}</p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Donut and Trend Charts */}
      {chartWidgets.length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {chartWidgets.map((w, index) => {
            const config = getWidgetUIConfig(w.key, w.title);
            const Icon = config.icon;
            const colSpan =
              chartWidgets.length % 2 === 1 && index === chartWidgets.length - 1
                ? "lg:col-span-2"
                : "";

            if (w.type === "DONUT") {
              const isDonutEmpty =
                w.total === 0 ||
                !w.segments ||
                w.segments.length === 0 ||
                w.segments.every((s) => s.value === 0);

              return (
                <div key={w.key} className={`card min-w-0 ${colSpan}`}>
                  <div className="flex items-center justify-between gap-2 mb-3 min-w-0">
                    <div className="flex items-center gap-2 min-w-0">
                      <Icon className="h-4 w-4 text-primary-500 shrink-0" aria-hidden="true" />
                      <h3 className="text-[15px] font-semibold text-[#10253A] min-w-0 truncate">{w.title}</h3>
                    </div>
                    {config.viewAllHref && (
                      <button
                        type="button"
                        onClick={() => navigate(config.viewAllHref!)}
                        className="text-sm font-medium text-primary-600 hover:text-primary-700 flex-shrink-0"
                      >
                        View all
                      </button>
                    )}
                  </div>
                  <div className="mb-4">
                    <OverviewStatStrip
                      items={(w.stats ?? []).map((s: DonutStat) => ({
                        key: s.key,
                        label: s.label,
                        value: s.value,
                        color: s.color,
                        icon: statIconElement(w.key, s.icon),
                      }))}
                    />
                  </div>

                  {isDonutEmpty ? (
                    <EmptyState
                      icon={Icon ? <Icon size={22} /> : undefined}
                      title={
                        w.title.toLowerCase().includes("doctor")
                          ? "No doctors registered yet"
                          : w.title.toLowerCase().includes("patient")
                          ? "No patients registered yet"
                          : w.title.toLowerCase().includes("mdt")
                          ? "No MDT reports recorded yet"
                          : `No ${w.title.toLowerCase()} recorded yet`
                      }
                      description={
                        w.title.toLowerCase().includes("doctor")
                          ? "Add your first doctor to enable MDT reviews, patient assignments, and consultations."
                          : w.title.toLowerCase().includes("patient")
                          ? "Add your first patient to begin tracking cases, records, and treatments."
                          : w.title.toLowerCase().includes("mdt")
                          ? "No MDT cases or reviews have been created yet."
                          : `No ${w.centerLabel ? w.centerLabel.toLowerCase() : "records"} have been added to this section yet.`
                      }
                      action={
                        w.title.toLowerCase().includes("doctor")
                          ? { label: "Add Doctor", to: "/doctors/new" }
                          : w.title.toLowerCase().includes("patient")
                          ? { label: "Add Patient", to: "/patients/new" }
                          : config.viewAllHref
                          ? { label: `View ${config.title}`, to: config.viewAllHref }
                          : undefined
                      }
                      variant="compact"
                    />
                  ) : (
                    <MDTDonut
                      segments={w.segments}
                      total={w.total}
                      centerLabel={w.centerLabel}
                      size={190}
                      thickness={28}
                    />
                  )}
                </div>
              );
            }

            const g = trendGranularity[w.key] ?? w.granularity ?? "month";
            return (
              <div key={w.key} className={`card min-w-0 ${colSpan}`}>
                <OnboardingLineChart
                  title={w.title}
                  icon={<Icon className="h-4 w-4" aria-hidden="true" />}
                  points={w.series?.[g] ?? []}
                  dates={w.dates}
                  color={w.color ?? "#15BBAE"}
                  granularity={g}
                  loading={loading}
                  error={error}
                  onRetry={handleRetry}
                  isStale={isStale}
                  onGranularityChange={(next) =>
                    setTrendGranularity((prev) => ({ ...prev, [w.key]: next }))
                  }
                />
              </div>
            );
          })}
        </div>
      )}

      {/* Activity and Submitted Lists */}
      {listWidgets.length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {listWidgets.map((w, index) => {
            const config = getWidgetUIConfig(w.key, w.title);
            const items = Array.isArray(w.items) ? w.items : [];
            const colSpan =
              listWidgets.length % 2 === 1 && index === listWidgets.length - 1
                ? "lg:col-span-2"
                : "";

            if (w.key === "AUDIT_LOG_LIST") {
              const activityViewAllHref = "/activity";
              return (
                <div key={w.key} className={colSpan}>
                  <RecentActivityOldUI
                    items={items}
                    viewAllHref={activityViewAllHref}
                    onViewAll={() => navigate(activityViewAllHref)}
                    twoColumn={listWidgets.length === 1}
                  />
                </div>
              );
            }

            if (w.key === "MDT_SUBMITTED_LIST") {
              const displayMdtItems = colSpan ? items.slice(0, 8) : items;
              return (
                <div key={w.key} className={`card overflow-hidden min-w-0 ${colSpan}`}>
                  <div className="flex items-center justify-between gap-2 mb-4 min-w-0">
                    <h3 className="text-lg font-semibold text-neutral-900 min-w-0 truncate">
                      {config.title}
                    </h3>
                    {config.viewAllHref && (
                      <button
                        type="button"
                        onClick={() => navigate(config.viewAllHref!)}
                        className="text-primary-600 hover:text-primary-700 text-sm font-medium flex-shrink-0"
                      >
                        View all
                      </button>
                    )}
                  </div>
                  <div
                    className={
                      colSpan
                        ? "grid grid-cols-1 sm:grid-cols-2 gap-3 min-w-0"
                        : "space-y-3 min-w-0"
                    }
                  >
                    {displayMdtItems.length === 0 ? (
                      <div className={colSpan ? "col-span-full" : ""}>
                        <EmptyState
                          icon={<FileText size={22} />}
                          title="No MDT submissions recorded"
                          description="No MDT reports or cases are currently submitted or pending review."
                          action={config.viewAllHref ? { label: "View Reports", to: config.viewAllHref } : undefined}
                          variant="compact"
                        />
                      </div>
                    ) : (
                      displayMdtItems.map((mdt: Record<string, unknown>) => (
                        <div
                          key={String(mdt.id)}
                          className="p-3 rounded-lg border-l-4 bg-primary-50 border-primary-400 min-w-0 overflow-hidden cursor-pointer hover:bg-primary-100/50 transition-colors"
                          onClick={() => mdt.id && navigate(`/reports/${mdt.id}`)}
                        >
                          <div className="flex items-center justify-between gap-2">
                            <p className="text-sm font-medium text-neutral-900 line-clamp-2 break-words">
                              {String(mdt.title ?? "MDT report submitted")}
                            </p>
                            {["approved", "final"].includes(String(mdt.status)) && (
                              <span className="inline-flex items-center px-2 py-0.5 rounded text-[11px] font-semibold bg-emerald-100 text-emerald-800 flex-shrink-0">
                                Approved
                              </span>
                            )}
                          </div>
                          <p className="text-xs text-neutral-600 mt-1 break-words">
                            Submitted by{" "}
                            <strong className="break-words">
                              {String(mdt.submittedBy ?? "—")}
                            </strong>
                          </p>
                          {mdt.submittedTo ? (
                            <p className="text-xs text-neutral-600 mt-0.5 break-words">
                              Submitted to{" "}
                              <strong className="break-words">
                                {String(mdt.submittedTo ?? "—")}
                              </strong>
                            </p>
                          ) : null}
                          <p className="text-xs text-neutral-500 mt-1">
                            {mdt.createdAt ? timeAgo(String(mdt.createdAt)) : ""}
                          </p>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              );
            }

            return (
              <div key={w.key} className={`card overflow-hidden min-w-0 ${colSpan}`}>
                <div className="flex items-center justify-between gap-2 mb-4 min-w-0">
                  <h3 className="text-lg font-semibold text-neutral-900 min-w-0 truncate">
                    {config.title}
                  </h3>
                  {config.viewAllHref && (
                    <button
                      type="button"
                      onClick={() => navigate(config.viewAllHref!)}
                      className="text-primary-600 hover:text-primary-700 text-sm font-medium flex-shrink-0"
                    >
                      View all
                    </button>
                  )}
                </div>
                <div className="space-y-3 min-w-0">
                  {items.length === 0 ? (
                    <EmptyState
                      icon={<FileText size={22} />}
                      title="No items found"
                      description="No records are currently available for this section."
                      variant="compact"
                    />
                  ) : (
                    items.map((item, i) => (
                      <ListWidgetItem
                        key={String((item as Record<string, unknown>)?.id ?? i)}
                        item={item}
                      />
                    ))
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default DoctorDashboard;
